/**
 * El aviso de disputa: la única cosa del panel que tiene fecha de caducidad.
 *
 * Va arriba del todo y de lado a lado, por encima incluso del menú lateral, y no
 * en la campana. La campana es para cosas que querrías saber —una venta, un
 * mensaje—; una disputa no es eso: Stripe da entre 7 y 21 días para subir
 * pruebas, y quien no responde pierde sin que nadie llegue a leer su caso. Un
 * plazo escondido detrás de un icono es un plazo que se cumple solo.
 *
 * Por eso tampoco se puede cerrar. Descartarlo no cambiaría nada de lo que lo
 * causó, y volvería a salir en la siguiente carga: lo único que se ganaría es
 * enseñar que este aviso se ignora, que es exactamente lo contrario de lo que
 * hace falta aquí.
 *
 * A dónde lleva:
 *   una disputa   → esa venta, abierta. Una lista de un elemento es un peaje.
 *   varias        → Cobros filtrado por disputadas, ordenadas por plazo.
 * En los dos casos el destino es una ficha NUESTRA y no la ventana de Stripe.
 * Stripe sabe del cargo; la ficha sabe qué oferta era y quién la compró, que es
 * lo que hace falta para decidir si esta disputa se pelea o se deja ir.
 */
import { useEffect, useState } from 'react'
import { listOpenDisputes, type OpenDispute } from '../../lib/api'
import { IconAlertSquareRoundedFilled } from '@tabler/icons-react'
import { useI18n } from '../../lib/i18n'
import type { Translate } from '@webbuilder/i18n'

/**
 * Cuánto queda de plazo, en las palabras en que aprieta.
 *
 * Días y no la fecha: «vence el 22 de agosto» obliga a restar mentalmente, y lo
 * que decide si esto se atiende ahora o luego es cuánto queda. Hoy y mañana se
 * dicen por su nombre porque son los dos casos en los que ya no hay margen.
 */
/* `t` entra por parámetro y no por hook: esto es una función suelta, fuera de
   ningún componente, y lo sigue siendo — pasar el traductor cuesta un argumento
   y evita convertirla en un hook que sólo se puede llamar al pintar. */
function dueLabel(dueBy: string | null, t: Translate): string | null {
  if (!dueBy) return null
  const when = new Date(dueBy).getTime()
  if (!Number.isFinite(when)) return null
  const startOfDay = (value: number) => {
    const date = new Date(value)
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  }
  const days = Math.round((startOfDay(when) - startOfDay(Date.now())) / 86_400_000)
  // Vencido y sin cerrar todavía: Stripe aún no ha resuelto, pero el vendedor ya
  // no llega. Se dice, en vez de enseñar «hace 2 días» como si fuera una fecha.
  if (days < 0) return t('dispute.overdue')
  if (days === 0) return t('dispute.dueToday')
  if (days === 1) return t('dispute.dueOne')
  return t('dispute.dueMany', { count: days })
}

export function DisputeBanner({ projectId, token, canReadSales, onOpenOrder, onOpenDisputes }: {
  projectId: string
  token: string
  /**
   * Si el rol de quien mira puede abrir Cobros. Sin ese permiso no hay a dónde
   * llevar a nadie. Era `isAdmin` —personal de véltica—, así que el vendedor
   * nunca se enteraba de una disputa con plazo corriendo.
   */
  canReadSales: boolean
  /** Abre ESA venta. Se usa cuando sólo hay una. */
  onOpenOrder: (orderId: string) => void
  /** Lleva a Cobros ya filtrado por disputadas. Se usa cuando hay varias. */
  onOpenDisputes: () => void
}) {
  const { t } = useI18n()
  const [disputes, setDisputes] = useState<OpenDispute[]>([])

  useEffect(() => {
    if (!canReadSales || !projectId || !token) return
    let alive = true
    listOpenDisputes(projectId, token)
      .then((rows) => { if (alive) setDisputes(rows) })
      // Callado a propósito: que no se pueda consultar no es una noticia que
      // dar, y un aviso rojo de error donde va el de disputas asusta por nada.
      .catch(() => {})
    return () => { alive = false }
  }, [canReadSales, projectId, token])

  if (disputes.length === 0) return null

  // Vienen ordenadas por plazo, así que la primera es la que se pierde antes:
  // es la que se abre con una sola, y la que da la fecha cuando hay varias.
  const [soonest] = disputes
  if (!soonest) return null
  const many = disputes.length > 1
  const due = dueLabel(soonest.disputeDueBy, t)

  return (
    <div className="dash-dispute-banner" role="alert">
      {/* El contenido va en un bloque centrado y no estirado de lado a lado: a
          1800px de ancho, un titular pegado a la izquierda y su acción pegada a
          la derecha son dos cosas que se leen por separado. Junto en el centro
          es una frase. El rayado de fuera es lo que impide que ese bloque
          parezca flotar en una banda vacía. */}
      <p className="dash-dispute-banner-inner">
        <span className="dash-dispute-banner-icon" aria-hidden="true">
          <IconAlertSquareRoundedFilled size={17} />
        </span>

        <strong>
          {many
            ? t('dispute.many', { count: disputes.length })
            : t('dispute.one', { amount: soonest.formatted })}
        </strong>

        <span className="dash-dispute-banner-sep" aria-hidden="true">·</span>

        <span className="dash-dispute-banner-detail">
          {many
            ? (due ? t('dispute.manyDue', { due }) : t('dispute.manyNoDue'))
            : (due
              /* La mayúscula la pone aquí quien la necesita: `due` es media
                 frase —«queda 1 día para responder»— y arranca oración sólo en
                 esta rama. Guardarla ya capitalizada en el catálogo la rompería
                 en la otra, donde va después de dos puntos. */
              ? t('dispute.oneDue', { due: `${due.charAt(0).toUpperCase()}${due.slice(1)}`, offer: soonest.offerTitle })
              : t('dispute.oneNoDue', { offer: soonest.offerTitle }))}
          {' '}
          {/* Enlace subrayado dentro de la frase y no un botón sólido: el aviso
              es una línea de texto, y un botón de color lo partía en dos
              mitades que competían. Sigue siendo un `button` porque no navega a
              ninguna dirección — abre una ficha. */}
          <button
            type="button"
            className="dash-dispute-banner-link"
            onClick={() => { if (many) onOpenDisputes(); else onOpenOrder(soonest.id) }}
          >
            {many ? t('dispute.viewAll') : t('dispute.respond')}
          </button>
        </span>
      </p>
    </div>
  )
}
